import { Alert, Box, Collapse } from "@outschool/backpack";
import { usePreviousValue, useScrollToElement } from "@outschool/ui-utils";
import React from "react";

import { usePageAlerts } from "./PageAlertsProvider";
import { PageAlertProps } from "./PageAlertTypes";

const DEFAULT_DURATION_SECONDS = 8;
const COLLAPSE_ANIMATION_MS = 500;

const PageAlert = ({
  id,
  content,
  dismissWillMute = false,
  dismissable = false,
  persistant = true,
  duration = DEFAULT_DURATION_SECONDS,
  onClose,
  scrollToParent,
  ...props
}: PageAlertProps & {
  scrollToParent: () => void;
}) => {
  const { removePageAlert, muteAlert, isMuted } = usePageAlerts();
  const [open, setOpen] = React.useState(false);

  const handleClose = dismissable
    ? (e: React.SyntheticEvent) => {
        setOpen(false);
        onClose?.(e);
        if (dismissWillMute) {
          muteAlert(id);
        }
      }
    : // Make undefined, otherwise Alert will render a close button
      undefined;

  React.useEffect(() => {
    if (!persistant) {
      const timeoutId = setTimeout(() => {
        setOpen(false);
        // Default duration to 12s
      }, duration * 1000);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      return () => {};
    }
  }, [persistant, duration]);

  React.useEffect(() => {
    // TODO - make it so PageAlerts are sticky to the top of the user's screen (under a sticky Header)
    // For now we will scroll to the alert so the user sees it.
    scrollToParent();
  }, [scrollToParent]);

  React.useEffect(() => {
    // This will set initial open state, as well as close self if it gets muted.
    // We open in an effect so it happens after initial render,
    // allowing the Collapse to animate in.
    setOpen(!isMuted(id));
  }, [id, isMuted]);

  const wasOpen = usePreviousValue(open);
  React.useEffect(() => {
    if (wasOpen && !open) {
      // Remove the alert from the pageAlerts context.
      // Do this in an effect, with a delay, so it happens after the Collapse animation.
      setTimeout(() => {
        removePageAlert(id);
      }, COLLAPSE_ANIMATION_MS);
    }
  }, [wasOpen, open, id, removePageAlert]);

  return (
    <Collapse
      in={open}
      sx={{
        width: "100%",
      }}
      timeout={COLLAPSE_ANIMATION_MS}
    >
      <Alert
        sx={{
          // Page width Alert styling
          borderRadius: 0,
          border: "none",
        }}
        onClose={handleClose}
        {...props}
      >
        {content}
      </Alert>
    </Collapse>
  );
};

const PageAlerts = () => {
  const { pageAlerts } = usePageAlerts();
  const pageAlertsEntries = Object.entries(pageAlerts);

  const scrollToElement = useScrollToElement();

  const scrollToParent = React.useCallback(() => {
    // Scroll to top of page, where the Alerts are
    scrollToElement(document.body);
  }, [scrollToElement]);

  if (pageAlertsEntries.length === 0) {
    return null;
  }

  return (
    <Box>
      {pageAlertsEntries.map(([id, pageAlert]) => (
        <PageAlert key={id} scrollToParent={scrollToParent} {...pageAlert} />
      ))}
    </Box>
  );
};

export default React.memo(PageAlerts);
